/**
 * Main App component - orchestrates the entire game
 */

import React, { useState, useEffect } from 'react';
import { GameBoard } from './components/game-board';
import { PlayerStatusPanel } from './components/player-status-panel';
import { GameModeSelection } from './components/game-mode-selection';
import { EyeTrackableButton } from './components/eye-trackable-button';
import { BackgroundMusicButton } from './components/background-music-button';
import { useGameLogic } from './hooks/use-game-logic';
import { createDifferentAvatars } from './utils/avatar-utils';
import { AssetsProvider } from './utils/assets-context';
import { preloadSounds, SoundEffect, playSound } from './utils/sound-utils';
import { IGameConfig, GameMode, PlayerType, PieceColor, GameStatus, AIDifficulty } from './types';
import './styles/global.scss';
import './app.scss';

function App() {
  return (
    <AssetsProvider>
      <GameApp />
    </AssetsProvider>
  );
}

function GameApp() {
  const {
    gameState,
    makeMove,
    startNewGame,
    resetGame,
    isAIThinking,
    setAIDifficulty,
    lastAIMove,
  } = useGameLogic();

  const [selectedMode, setSelectedMode] = useState<GameMode | null>(null);
  const [gameConfig, setGameConfig] = useState<IGameConfig | null>(null);

  // Preload sound effects when the component mounts
  useEffect(() => {
    preloadSounds([
      SoundEffect.DropCoin,
      SoundEffect.SelectColumnButton,
      SoundEffect.WinGame01,
      SoundEffect.WinGame02,
      SoundEffect.GameOver01,
      SoundEffect.GameOver02,
      SoundEffect.MyTurn01,
      SoundEffect.MyTurn02,
    ]);
  }, []);

  const handleModeSelect = (mode: GameMode, difficulty?: AIDifficulty) => {
    setSelectedMode(mode);

    // Set AI difficulty BEFORE starting the game if provided
    if (difficulty) {
      setAIDifficulty(difficulty);
    }

    // Create two different avatars for the players
    const [avatar1, avatar2] = createDifferentAvatars();

    // Create game configuration with different avatars
    const config: IGameConfig = {
      mode,
      player1: {
        id: 1,
        name: 'Player 1',
        type: PlayerType.Human,
        color: PieceColor.Red,
        avatar: avatar1,
      },
      player2: {
        id: 2,
        name: mode === GameMode.PlayerVsComputer ? 'Computer' : 'Player 2',
        type: mode === GameMode.PlayerVsComputer ? PlayerType.Computer : PlayerType.Human,
        color: PieceColor.Yellow,
        avatar: avatar2,
      },
      rows: 6,
      columns: 7,
    };

    setGameConfig(config);
    startNewGame(config);
  };

  const handleColumnClick = (column: number) => {
    // Play the drop coin sound effect when a move is made
    playSound(SoundEffect.DropCoin);
    makeMove(column);
  };

  const handleNewGame = () => {
    if (gameConfig) {
      startNewGame(gameConfig);
    }
  };

  const handleBackToMenu = () => {
    setSelectedMode(null);
    setGameConfig(null);
  };

  // Show mode selection if no mode is selected
  if (!selectedMode || !gameConfig) {
    return (
      <div className='app'>
        <GameModeSelection onModeSelect={handleModeSelect} selectedMode={selectedMode} />
        <BackgroundMusicButton />
      </div>
    );
  }

  const isGameActive = gameState.status === GameStatus.InProgress && !isAIThinking;

  return (
    <div className='app'>
      <div className='app__container'>
        {/* Absolute positioned buttons - moved to bottom for board view */}
        <EyeTrackableButton
          className='btn btn--secondary app__button app__button--new-game eye-trackable-button--positioned'
          onClick={handleNewGame}
          variant='secondary'
          data-testid='new-game-button'
          data-gaze-region='true'
          data-gaze-region-margin='1vw'
          ariaLabel='Start a new game'
        >
          🎮 New Game
        </EyeTrackableButton>
        <EyeTrackableButton
          className='btn btn--primary app__button app__button--menu eye-trackable-button--positioned'
          onClick={handleBackToMenu}
          variant='primary'
          data-testid='back-to-menu-button'
          data-gaze-region='true'
          data-gaze-region-margin='1vw'
          ariaLabel='Go back to main menu'
        >
          🏠 Menu
        </EyeTrackableButton>

        {/* Main Game Area */}
        <main className='app__main'>
          {/* AI Thinking Indicator */}
          {isAIThinking && (
            <div className='app__ai-thinking'>
              <div className='loading-spinner'></div>
              <span>Computer is thinking...</span>
            </div>
          )}

          {/* Game Area with Player Panels and Board */}
          <div className='app__game-area'>
            {/* Left Player Status Panel */}
            <div className='player-status-panel player-status-panel--left'>
              <PlayerStatusPanel
                player1={gameConfig.player1}
                currentPlayer={gameState.currentPlayer}
                gameStatus={gameState.status}
                position='left'
              />
            </div>

            {/* Game Board */}
            <GameBoard
              board={gameState.board}
              onColumnClick={handleColumnClick}
              winningLine={gameState.winningLine}
              isGameActive={isGameActive}
              currentPlayer={gameState.currentPlayer}
              gameConfig={gameConfig}
              gameStatus={gameState.status}
              winner={gameState.winner}
              lastAIMove={lastAIMove}
            />

            {/* Right Player Status Panel */}
            <div className='player-status-panel player-status-panel--right'>
              <PlayerStatusPanel
                player2={gameConfig.player2}
                currentPlayer={gameState.currentPlayer}
                gameStatus={gameState.status}
                position='right'
              />
            </div>
          </div>
        </main>

        {/* Background Music Button - only show on board view */}
        {/* Note: Music button is now only shown in mode selection */}
      </div>
    </div>
  );
}

export default App;
